import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { API_URL } from '../config/api';
import { FiRefreshCw, FiCalendar } from 'react-icons/fi';
import './Graficos.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const MESES_PT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const hojeLocalYYYYMMDD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const Graficos = () => {
  const [dadosPorHora, setDadosPorHora] = useState([]);
  const [dadosPorMes, setDadosPorMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dataFiltro, setDataFiltro] = useState(hojeLocalYYYYMMDD());
  const [anoFiltro, setAnoFiltro] = useState(() => new Date().getFullYear());

  const fetchDados = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sysconect/adm-login';
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [resHora, resMes] = await Promise.all([
        axios.get(`${API_URL}/api/acessos/por-hora`, {
          headers,
          params: { data: dataFiltro }
        }),
        axios.get(`${API_URL}/api/acessos/por-mes`, {
          headers,
          params: { ano: anoFiltro }
        })
      ]);
      setDadosPorHora(resHora.data || []);
      setDadosPorMes(resMes.data || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/sysconect/adm-login';
        return;
      }
      setError('Erro ao carregar dados dos gráficos. Tente novamente.');
      setDadosPorHora([]);
      setDadosPorMes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFiltro, anoFiltro]);

  const formatarHora = (hora) => {
    return `${String(hora).padStart(2, '0')}:00`;
  };

  const labels = dadosPorHora.map(item => formatarHora(item.hora));
  const quantidades = dadosPorHora.map(item => item.quantidade);
  const totalAcessos = quantidades.reduce((sum, qtd) => sum + qtd, 0);

  const labelsMeses = dadosPorMes.map((item) => MESES_PT[item.mes - 1] || `Mês ${item.mes}`);
  const quantidadesMes = dadosPorMes.map((item) => item.quantidade);
  const totalAno = quantidadesMes.reduce((sum, q) => sum + q, 0);

  const dadosGrafico1 = {
    labels: labels,
    datasets: [
      {
        label: 'Acessos',
        data: quantidades,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const dadosGrafico2 = {
    labels: labels,
    datasets: [
      {
        label: 'Acessos',
        data: quantidades,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const dadosGraficoMensal = {
    labels: labelsMeses,
    datasets: [
      {
        label: 'Acessos no ano',
        data: quantidadesMes,
        backgroundColor: 'rgba(153, 102, 255, 0.55)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1
      }
    ]
  };

  const opcoesGraficoMensal = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: {
        display: true,
        text: `Acessos mensais — ${anoFiltro}`,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            return `Acessos: ${ctx.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      },
      x: {
        title: {
          display: true,
          text: 'Mês'
        }
      }
    }
  };

  const opcoesGrafico = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: `Acessos Diários por Hora - ${new Date(dataFiltro).toLocaleDateString('pt-BR')}`,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Acessos: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        }
      },
      x: {
        title: {
          display: true,
          text: 'Hora do Dia'
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="graficos-container">
        <div className="loading">Carregando gráficos...</div>
      </div>
    );
  }

  return (
    <div className="graficos-container">
      <div className="graficos-header">
        <div>
          <h1>Gráficos de Acessos</h1>
          <p>Visualize os acessos por hora do dia e o consolidado mensal no ano</p>
        </div>
        <div className="graficos-actions">
          <div className="filtro-data">
            <FiCalendar />
            <input
              type="date"
              value={dataFiltro}
              onChange={(e) => setDataFiltro(e.target.value)}
              className="data-input"
              title="Dia para os gráficos por hora"
            />
          </div>
          <div className="filtro-data filtro-ano">
            <label htmlFor="graficos-ano" className="filtro-ano-label">Ano (mensal)</label>
            <input
              id="graficos-ano"
              type="number"
              min={2000}
              max={2100}
              value={anoFiltro}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setAnoFiltro(Number.isFinite(n) ? Math.min(2100, Math.max(2000, n)) : new Date().getFullYear());
              }}
              className="data-input filtro-ano-input"
            />
          </div>
          <button className="refresh-btn" onClick={fetchDados} title="Recarregar">
            <FiRefreshCw /> Recarregar
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="graficos-stats">
        <div className="stat-card">
          <div className="stat-value">{totalAcessos}</div>
          <div className="stat-label">Total de Acessos</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {dadosPorHora.length > 0 
              ? dadosPorHora.reduce((max, item) => item.quantidade > max ? item.quantidade : max, 0)
              : 0
            }
          </div>
          <div className="stat-label">Pico de Acessos</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {dadosPorHora.length > 0
              ? formatarHora(
                  dadosPorHora.reduce((maxItem, item) => 
                    item.quantidade > maxItem.quantidade ? item : maxItem, 
                    dadosPorHora[0]
                  ).hora
                )
              : '-'
            }
          </div>
          <div className="stat-label">Hora de Pico</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalAno}</div>
          <div className="stat-label">Total no ano {anoFiltro}</div>
        </div>
      </div>

      <div className="graficos-grid">
        <div className="grafico-card">
          <h2>Gráfico 1: Acessos Diários por Hora (Barras)</h2>
          <div className="grafico-wrapper">
            <Bar data={dadosGrafico1} options={opcoesGrafico} />
          </div>
        </div>

        <div className="grafico-card">
          <h2>Gráfico 2: Acessos Diários por Hora (Linha)</h2>
          <div className="grafico-wrapper">
            <Line data={dadosGrafico2} options={opcoesGrafico} />
          </div>
        </div>

        <div className="grafico-card">
          <h2>Gráfico 3: Acessos Mensais (Barras)</h2>
          <p className="grafico-card-desc">Total de acessos em cada mês do ano selecionado</p>
          <div className="grafico-wrapper">
            <Bar data={dadosGraficoMensal} options={opcoesGraficoMensal} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graficos;
